import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getPackages } from "@/services/admin/packages.service";
import { Package } from "@/services/admin/packages.interfaces";

interface PackageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedPackages: Package[]) => void;
  currentPackages?: Package[];
}

const PackageSelectionDialog: React.FC<PackageSelectionDialogProps> = ({ isOpen, onClose, onConfirm }) => {
  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<Package[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const packages = await getPackages();
        setAvailablePackages(packages);
      } catch (error) {
        console.error("Error loading packages:", error);
      }
    };

    loadPackages();
  }, []);

  const handlePackageSelect = (pkg: Package) => {
    setSelectedPackages((prevSelected) => {
      if (prevSelected.some((p) => p.id === pkg.id)) {
        return prevSelected.filter((p) => p.id !== pkg.id);
      } else {
        return [...prevSelected, pkg];
      }
    });
  };

  const filteredPackages = availablePackages.filter((pkg) =>
    pkg.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select Packages</DialogTitle>
          <DialogDescription>
            Select the packages you want to add to the student. You can search for packages and select multiple packages at once.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Search packages"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="space-y-2">
            {filteredPackages.map((pkg) => (
              <div key={pkg.id} className="flex items-center justify-between">
                <Label>{pkg.name}</Label>
                <Button
                  variant={selectedPackages.some((p) => p.id === pkg.id) ? "default" : "outline"}
                  onClick={() => handlePackageSelect(pkg)}
                >
                  {selectedPackages.some((p) => p.id === pkg.id) ? "Selected" : "Select"}
                </Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={() => {
              onConfirm(selectedPackages);
              onClose();
            }}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PackageSelectionDialog;
